"use client"

import { motion } from "framer-motion"
import { Shield, Sword, Users, Zap, Coins, Skull, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import Link from "next/link"

const servers = [
    {
        id: "rhaegal",
        name: "Rhaegal",
        tagline: "The Eternal Path",
        description: "An official-like experience focused on balanced progression and classic mechanics. No dragon weapons, cursed weapons, or L-grade items.",
        rates: {
            exp: "x3",
            sp: "x3",
            adena: "x1",
            drop: "x1",
        },
        features: [
            "Balanced Retail Progression",
            "No Dragon Weapons",
            "No L-Grade Armor/Weapons",
            "No Cursed Weapons",
        ],
        theme: {
            bg: "bg-gradient-to-br from-slate-900/80 via-blue-900/40 to-slate-950/80",
            border: "border-blue-400/30",
            accent: "text-blue-300",
            glow: "shadow-blue-900/50",
            button: "bg-blue-600 hover:bg-blue-500 text-white",
            icon: <Shield className="w-8 h-8 text-blue-400 mb-4" />
        }
    },
    {
        id: "silverwing",
        name: "Silverwing",
        tagline: "The Crimson Battlefield",
        description: "A high-rate, pure PvP slaughterhouse. No limitations on items or power. Enter the fray and prove your dominance.",
        rates: {
            exp: "x963",
            sp: "x963",
            adena: "x500",
            drop: "x10",
        },
        features: [
            "Pure PvP Action",
            "No Item Limitations",
            "All Gear Accessible",
            "Massive Clan Wars",
        ],
        theme: {
            bg: "bg-gradient-to-br from-red-950/80 via-red-900/40 to-slate-900/80",
            border: "border-red-500/30",
            accent: "text-red-400",
            glow: "shadow-red-900/50",
            button: "bg-red-600 hover:bg-red-500 text-white",
            icon: <Sword className="w-8 h-8 text-red-500 mb-4" />
        }
    }
]

export function ServerShowcase() {
    return (
        <section className="py-24 relative z-10 w-full overflow-hidden">
            <div className="container mx-auto px-4">

                <div className="text-center mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-4xl md:text-5xl font-black font-cinzel mb-4"
                    >
                        CHOOSE YOUR <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">REALM</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-slate-400 max-w-2xl mx-auto text-lg"
                    >
                        Two distinct worlds, one legendary legacy. Whether you seek instant battlefield glory or the classic journey of progression, your epic awaits.
                    </motion.p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                    {servers.map((server, index) => (
                        <motion.div
                            key={server.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.2 }}
                            className={`relative rounded-xl border ${server.theme.border} backdrop-blur-md overflow-hidden group ${server.theme.bg} transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${server.theme.glow}`}
                        >
                            <div className="p-8">
                                {/* Header */}
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        {server.theme.icon}
                                        <h3 className="text-3xl font-black font-cinzel text-white mb-1 group-hover:scale-105 transition-transform origin-left">{server.name}</h3>
                                        <p className={`font-semibold tracking-wider uppercase text-sm ${server.theme.accent}`}>{server.tagline}</p>
                                    </div>
                                </div>

                                <p className="text-slate-300 mb-8 min-h-[80px]">
                                    {server.description}
                                </p>

                                {/* Rates Grid */}
                                <div className="grid grid-cols-4 gap-2 mb-8 bg-black/40 p-4 rounded-lg border border-white/5">
                                    <div className="text-center">
                                        <div className="text-xs text-slate-400 uppercase font-bold mb-1">EXP</div>
                                        <div className={`font-black font-cinzel ${server.theme.accent}`}>{server.rates.exp}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-xs text-slate-400 uppercase font-bold mb-1">SP</div>
                                        <div className={`font-black font-cinzel ${server.theme.accent}`}>{server.rates.sp}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-xs text-slate-400 uppercase font-bold mb-1">Adena</div>
                                        <div className={`font-black font-cinzel ${server.theme.accent}`}>{server.rates.adena}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-xs text-slate-400 uppercase font-bold mb-1">Drop</div>
                                        <div className={`font-black font-cinzel ${server.theme.accent}`}>{server.rates.drop}</div>
                                    </div>
                                </div>

                                {/* Features List */}
                                <ul className="space-y-3 mb-8">
                                    {server.features.map((feature, i) => (
                                        <li key={i} className="flex items-center text-slate-300 text-sm">
                                            <Zap className={`w-4 h-4 mr-3 ${server.theme.accent}`} />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                {/* Action */}
                                <Link href={`/guides/${server.id}`}>
                                    <Button className={`w-full py-6 font-bold uppercase tracking-wider ${server.theme.button}`}>
                                        Explore {server.name} <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>

                            </div>

                            {/* Subtle hover effect overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}
